import React, { useState, useMemo } from "react";
import {
  Layers,
  CheckCircle2,
  AlertTriangle,
  Info,
  Search,
  CheckSquare,
  Square,
  Sparkles,
  FileCheck2,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Check,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/components/ui/Card";
import { Input } from "src/components/ui/Input";
import { Label } from "src/components/ui/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/Select";
import {
  EnrollmentCurriculumTarget,
  EnrollmentExamBlueprint,
  CurriculumTargetType,
} from "src/services/manualEnrollmentService";

interface EnrollmentCurriculumTreeProps {
  productType?: "booster" | "custom" | "preparation";
  scopeId: string;
  targets: EnrollmentCurriculumTarget[];
  targetKeys: string[];
  onToggleTarget: (key: string) => void;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  blueprints: EnrollmentExamBlueprint[];
  blueprintId: string;
  onBlueprintChange: (id: string) => void;
  isLoadingTargets?: boolean;
}

const targetTypeBadges: Record<CurriculumTargetType, { label: string; bg: string; text: string; border: string }> = {
  domain: { label: "Domain", bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
  unit: { label: "Unit", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  lesson: { label: "Lesson", bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200" },
  objective: { label: "Objective", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
};

export const EnrollmentCurriculumTree: React.FC<EnrollmentCurriculumTreeProps> = ({
  productType,
  scopeId,
  targets,
  targetKeys,
  onToggleTarget,
  onSelectAll,
  onDeselectAll,
  blueprints,
  blueprintId,
  onBlueprintChange,
  isLoadingTargets = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSyllabusDetails, setShowSyllabusDetails] = useState(true);

  const boosterObjectives = useMemo(
    () => targets.filter((t) => t.type === "objective"),
    [targets]
  );
  const boosterDomains = useMemo(
    () => targets.filter((t) => t.type === "domain"),
    [targets]
  );
  const boosterUnits = useMemo(
    () => targets.filter((t) => t.type === "unit"),
    [targets]
  );

  const filteredTargets = useMemo(() => {
    if (!searchTerm.trim()) return targets;
    const term = searchTerm.toLowerCase();
    return targets.filter(
      (t) => t.label.toLowerCase().includes(term) || t.detail.toLowerCase().includes(term)
    );
  }, [targets, searchTerm]);

  // Count selected targets by type
  const targetCounts = useMemo(() => {
    const counts = { domain: 0, unit: 0, lesson: 0, objective: 0 };
    for (const key of targetKeys) {
      const type = key.split(":")[0] as CurriculumTargetType;
      if (counts[type] !== undefined) counts[type]++;
    }
    return counts;
  }, [targetKeys]);

  return (
    <Card className="border-slate-200/90 shadow-sm overflow-hidden bg-white">
      <CardHeader className="bg-slate-50/60 border-b border-slate-100 py-4 px-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xs">
              2
            </div>
            <div>
              <CardTitle className="text-sm font-bold text-slate-900">
                Canonical Curriculum Specification
              </CardTitle>
              <CardDescription className="text-xs text-slate-500 mt-0.5">
                Resolution pipeline (<span className="font-mono text-[11px]">Subject → Domain → Unit → Lesson → Objective</span>).
              </CardDescription>
            </div>
          </div>
          <span className="text-[11px] font-mono font-medium text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded">
            STEP 02/03
          </span>
        </div>
      </CardHeader>

      <CardContent className="p-5">
        {!productType ? (
          <div className="text-center py-8 text-slate-400 text-xs">
            Select a product above to configure curriculum plan targets.
          </div>
        ) : productType === "booster" ? (
          /* ========================================================================= */
          /* BOOSTER (HIGH DOSAGE TUTORING) */
          /* ========================================================================= */
          !scopeId ? (
            <div className="p-6 text-center text-xs text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
              Please select a published curriculum scope above in Step 1 to load the curriculum syllabus and objectives.
            </div>
          ) : isLoadingTargets ? (
            <div className="p-8 text-center text-xs text-slate-500 bg-slate-50 rounded-xl">
              Fetching published curriculum scope structure and teachable objectives...
            </div>
          ) : targets.length === 0 ? (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
              <span>No published objectives found for this scope.</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Booster Announcement Header */}
              <div className="p-4 rounded-xl bg-purple-50/70 border border-purple-200/80 flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h4 className="text-xs font-bold text-purple-950">
                      Full Published Scope Auto-Enrollment (Booster Mode)
                    </h4>
                    <span className="text-[11px] font-mono bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-semibold border border-purple-200">
                      {boosterObjectives.length} Objectives Auto-Enrolled
                    </span>
                  </div>
                  <p className="text-xs text-purple-800/90 mt-1 leading-relaxed">
                    High Dosage Tutoring automatically enrolls <strong>all {boosterObjectives.length} published objectives</strong> from the selected scope across <strong>{boosterDomains.length} domains</strong> and <strong>{boosterUnits.length} units</strong>. The entire syllabus is frozen upon plan finalization.
                  </p>
                </div>
              </div>

              {/* Telemetry Metric Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">
                      Teachable Objectives
                    </span>
                    <span className="text-lg font-bold font-mono text-purple-700">
                      {boosterObjectives.length}
                    </span>
                  </div>
                  <span className="text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Check className="w-3 h-3 text-emerald-600" /> 100% Enrolled
                  </span>
                </div>

                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">
                      Curriculum Domains
                    </span>
                    <span className="text-lg font-bold font-mono text-slate-900">
                      {boosterDomains.length}
                    </span>
                  </div>
                  <span className="text-[11px] font-mono text-slate-500 bg-white border border-slate-200 px-1.5 py-0.5 rounded">
                    Published
                  </span>
                </div>

                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">
                      Syllabus Units
                    </span>
                    <span className="text-lg font-bold font-mono text-slate-900">
                      {boosterUnits.length}
                    </span>
                  </div>
                  <span className="text-[11px] font-mono text-slate-500 bg-white border border-slate-200 px-1.5 py-0.5 rounded">
                    Included
                  </span>
                </div>
              </div>

              {/* Scope Syllabus Breakdown Card */}
              <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                <div className="p-3 bg-slate-50/80 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-purple-600" />
                    <span className="text-xs font-bold text-slate-800">
                      Scope Syllabus Structure ({boosterDomains.length} Domains)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowSyllabusDetails(!showSyllabusDetails)}
                    className="text-[11px] text-purple-700 font-semibold hover:underline flex items-center gap-1"
                  >
                    {showSyllabusDetails ? (
                      <>
                        <span>Collapse Syllabus</span>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </>
                    ) : (
                      <>
                        <span>Expand Syllabus</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </>
                    )}
                  </button>
                </div>

                {showSyllabusDetails && (
                  <div className="p-3 space-y-2 max-h-64 overflow-y-auto divide-y divide-slate-100">
                    {boosterDomains.map((domain) => {
                      const unitsInDomain = boosterUnits.filter((u) => u.detail.includes(domain.label));
                      return (
                        <div key={domain.id} className="pt-2 first:pt-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">
                                Domain
                              </span>
                              <span className="text-xs font-bold text-slate-900">{domain.label}</span>
                            </div>
                            <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Check className="w-3 h-3 text-emerald-600" /> Auto-Enrolled
                            </span>
                          </div>

                          {unitsInDomain.length > 0 && (
                            <div className="mt-1.5 ml-4 pl-3 border-l-2 border-purple-200 space-y-1">
                              {unitsInDomain.map((unit) => (
                                <div key={unit.id} className="flex items-center justify-between text-[11px] text-slate-600 py-0.5">
                                  <span className="truncate max-w-[320px]">{unit.label}</span>
                                  <span className="font-mono text-[10px] text-slate-400">Unit included</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Snapshot Target & Integrity Notice */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-slate-700">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>
                    Snapshot Target: Exactly <strong>{boosterObjectives.length} teachable objectives</strong> will be frozen upon activation.
                  </span>
                </div>
                <span className="font-mono text-[11px] bg-white px-2.5 py-1 rounded border border-slate-200 text-slate-600 font-medium">
                  Auto-Resolved & Locked
                </span>
              </div>
            </div>
          )
        ) : productType === "custom" ? (
          /* ========================================================================= */
          /* CUSTOM (PERSONALIZED PLAN) */
          /* ========================================================================= */
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <Label className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-purple-600" />
                  <span>Curriculum Selection Targets</span>
                  <span className="text-red-500 font-bold">*</span>
                </Label>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Select any domain, unit, lesson, or objective. Overlaps are deduplicated into a single objective set.
                </p>
              </div>

              {targets.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onSelectAll}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 px-2 py-1 rounded border border-purple-200 transition"
                  >
                    <CheckSquare className="w-3 h-3" /> Select All
                  </button>
                  <button
                    type="button"
                    onClick={onDeselectAll}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded border border-slate-200 transition"
                  >
                    <Square className="w-3 h-3" /> Clear
                  </button>
                </div>
              )}
            </div>

            {/* Target Search and Stats Bar */}
            {targets.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
                <div className="relative w-full sm:w-64">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
                  <Input
                    placeholder="Search topics, units, objectives..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 text-xs h-8 rounded-lg bg-slate-50 border-slate-200 focus:bg-white"
                  />
                </div>
                <div className="text-[11px] text-slate-600 flex items-center gap-1.5">
                  <span className="font-semibold text-slate-900">{targetKeys.length}</span> of{" "}
                  <span>{targets.length} targets selected</span>
                  {targetKeys.length > 0 && (
                    <span className="text-purple-700 font-medium ml-1">
                      ({targetCounts.domain}D · {targetCounts.unit}U · {targetCounts.lesson}L · {targetCounts.objective}O)
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Target List Container */}
            {!scopeId ? (
              <div className="p-6 text-center text-xs text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                Please select a published curriculum scope above to browse teachable units.
              </div>
            ) : isLoadingTargets ? (
              <div className="p-8 text-center text-xs text-slate-500 bg-slate-50 rounded-xl">
                Loading curriculum hierarchy...
              </div>
            ) : targets.length === 0 ? (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>No published curriculum targets were found for this scope.</span>
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto space-y-1.5 rounded-xl border border-slate-200 bg-slate-50/40 p-2 divide-y divide-slate-100">
                {filteredTargets.map((target) => {
                  const key = `${target.type}:${target.id}`;
                  const isChecked = targetKeys.includes(key);
                  const badge = targetTypeBadges[target.type] || targetTypeBadges.objective;

                  return (
                    <label
                      key={key}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg p-2.5 transition pt-2.5 ${
                        isChecked
                          ? "bg-purple-50/50 border border-purple-200/80 shadow-xs"
                          : "bg-white hover:bg-slate-50/80 border border-slate-200/60"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => onToggleTarget(key)}
                        className="mt-1 h-3.5 w-3.5 rounded text-purple-600 focus:ring-purple-500 border-slate-300"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border ${badge.bg} ${badge.text} ${badge.border}`}
                          >
                            {badge.label}
                          </span>
                          <span className="text-xs font-semibold text-slate-900 truncate">
                            {target.label}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                          {target.detail}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Prerequisite & Resolution Advisory */}
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-start gap-2 text-xs text-slate-600">
              <Info className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <span className="font-semibold text-slate-800">Prerequisite Guarantee:</span> Direct prerequisites are automatically bundled into the frozen snapshot. An authorized actor may omit a prerequisite only with an audited reason.
              </div>
            </div>
          </div>
        ) : (
          /* ========================================================================= */
          /* PREPARATION (EXAM BLUEPRINT) */
          /* ========================================================================= */
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                <FileCheck2 className="w-3.5 h-3.5 text-blue-600" />
                <span>Approved Exam Blueprint</span>
                <span className="text-red-500 font-bold">*</span>
              </Label>
              <Select value={blueprintId} onValueChange={onBlueprintChange}>
                <SelectTrigger className="w-full text-xs bg-white border-slate-200 rounded-xl shadow-none focus:ring-2 focus:ring-purple-200">
                  <SelectValue placeholder="Select published exam blueprint approved for this product..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {blueprints.map((blueprint) => (
                    <SelectItem key={blueprint.id} value={blueprint.id} className="text-xs">
                      <span className="font-semibold text-slate-900">{blueprint.name}</span>
                      <span className="text-slate-400 ml-2">({blueprint.version_label})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {blueprints.length === 0 ? (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>This product does not have any approved published exam blueprints.</span>
              </div>
            ) : (
              <div className="p-3 rounded-xl bg-blue-50/70 border border-blue-200 text-xs text-blue-900 flex items-center justify-between">
                <span>Blueprint defines standardized diagnostic weights and exam target milestones.</span>
                <span className="font-mono text-[11px] bg-white px-2 py-0.5 rounded border border-blue-200 font-medium">
                  Approved Standard
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

